import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Mail, Download } from 'lucide-react';

/**
 * ShippingLabelModal — generate or email a PDF shipping label.
 *
 * Props:
 *   labelFormData    — current label form state object
 *   setLabelFormData — state setter for labelFormData
 *   onSubmit         — (e) => void  (form submit handler)
 *   onClose          — () => void
 */
const ShippingLabelModal = ({ labelFormData, setLabelFormData, onSubmit, onClose }) => {
  const { t } = useTranslation();

  /** Helper: update a nested sender/recipient field. */
  const setParty = (party, field, value) =>
    setLabelFormData({
      ...labelFormData,
      [party]: { ...labelFormData[party], [field]: value },
    });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content quote-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '90vh', overflowY: 'auto', maxWidth: '800px' }}
      >
        {/* ── Modal header ─────────────────────────────────── */}
        <div className="modal-header">
          <h2>
            <FileText size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            {t('admin.shipping.labelTitle')}
          </h2>
          <button onClick={onClose} className="modal-close" aria-label={t('admin.common.close')}>×</button>
        </div>

        <form onSubmit={onSubmit} className="quote-form">

          {/* ── Shipment Reference ──────────────────────────── */}
          <div className="form-section">
            <h3>{t('admin.shipping.shipmentInfo')}</h3>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.referenceNumber')} *</label>
                <input
                  type="text"
                  value={labelFormData.referenceNumber}
                  onChange={(e) => setLabelFormData({ ...labelFormData, referenceNumber: e.target.value })}
                  placeholder="ORD-2025-001"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.weight')} (kg) *</label>
                <input
                  type="number"
                  value={labelFormData.weight}
                  onChange={(e) => setLabelFormData({ ...labelFormData, weight: e.target.value })}
                  step="0.1"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.shippingCost')} (€)</label>
                <input
                  type="number"
                  value={labelFormData.shippingCost}
                  onChange={(e) => setLabelFormData({ ...labelFormData, shippingCost: e.target.value })}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.internalTracking')}</label>
                <input
                  type="text"
                  value={labelFormData.trackingNumber}
                  onChange={(e) => setLabelFormData({ ...labelFormData, trackingNumber: e.target.value })}
                  placeholder="MFLX-2025-001"
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.carrierTracking')}</label>
                <input
                  type="text"
                  value={labelFormData.carrierTrackingNumber}
                  onChange={(e) => setLabelFormData({ ...labelFormData, carrierTrackingNumber: e.target.value })}
                  placeholder="DHL123456789"
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.zone')}</label>
                <input
                  type="text"
                  value={labelFormData.zone}
                  onChange={(e) => setLabelFormData({ ...labelFormData, zone: e.target.value })}
                  placeholder="Italy South"
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.shipping.specialInstructions')}</label>
              <textarea
                value={labelFormData.specialInstructions}
                onChange={(e) => setLabelFormData({ ...labelFormData, specialInstructions: e.target.value })}
                rows={2}
                placeholder={t('admin.shipping.specialInstructionsPlaceholder')}
              />
            </div>
          </div>

          {/* ── Sender Information ──────────────────────────── */}
          <div className="form-section">
            <h3>{t('admin.shipping.senderInfo')}</h3>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.senderCompany')}</label>
                <input
                  type="text"
                  value={labelFormData.sender.companyName}
                  onChange={(e) => setParty('sender', 'companyName', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.senderName')}</label>
                <input
                  type="text"
                  value={labelFormData.sender.name}
                  onChange={(e) => setParty('sender', 'name', e.target.value)}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.shipping.senderAddress')}</label>
              <input
                type="text"
                value={labelFormData.sender.address}
                onChange={(e) => setParty('sender', 'address', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.senderPostalCode')}</label>
                <input
                  type="text"
                  value={labelFormData.sender.postalCode}
                  onChange={(e) => setParty('sender', 'postalCode', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.senderCity')}</label>
                <input
                  type="text"
                  value={labelFormData.sender.city}
                  onChange={(e) => setParty('sender', 'city', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.senderCountry')}</label>
                <input
                  type="text"
                  value={labelFormData.sender.country}
                  onChange={(e) => setParty('sender', 'country', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.senderPhone')}</label>
                <input
                  type="tel"
                  value={labelFormData.sender.phone}
                  onChange={(e) => setParty('sender', 'phone', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.senderEmail')}</label>
                <input
                  type="email"
                  value={labelFormData.sender.email}
                  onChange={(e) => setParty('sender', 'email', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Recipient Information ───────────────────────── */}
          <div className="form-section">
            <h3>{t('admin.shipping.recipientInfo')} *</h3>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.recipientCompany')}</label>
                <input
                  type="text"
                  value={labelFormData.recipient.companyName}
                  onChange={(e) => setParty('recipient', 'companyName', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.recipientName')} *</label>
                <input
                  type="text"
                  value={labelFormData.recipient.name}
                  onChange={(e) => setParty('recipient', 'name', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.shipping.recipientAddress')} *</label>
              <input
                type="text"
                value={labelFormData.recipient.address}
                onChange={(e) => setParty('recipient', 'address', e.target.value)}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.recipientPostalCode')} *</label>
                <input
                  type="text"
                  value={labelFormData.recipient.postalCode}
                  onChange={(e) => setParty('recipient', 'postalCode', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.recipientCity')} *</label>
                <input
                  type="text"
                  value={labelFormData.recipient.city}
                  onChange={(e) => setParty('recipient', 'city', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('admin.shipping.recipientCountry')} *</label>
                <input
                  type="text"
                  value={labelFormData.recipient.country}
                  onChange={(e) => setParty('recipient', 'country', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.shipping.recipientPhone')}</label>
                <input
                  type="tel"
                  value={labelFormData.recipient.phone}
                  onChange={(e) => setParty('recipient', 'phone', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>
                  {t('admin.shipping.recipientEmail')}
                  {labelFormData.sendEmail ? ' *' : ''}
                </label>
                <input
                  type="email"
                  value={labelFormData.recipient.email}
                  onChange={(e) => setParty('recipient', 'email', e.target.value)}
                  required={labelFormData.sendEmail}
                />
              </div>
            </div>
          </div>

          {/* ── Email Options ───────────────────────────────── */}
          <div
            className="form-section"
            style={{ background: '#1e293b', borderRadius: '8px', padding: '1.5rem' }}
          >
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={labelFormData.sendEmail}
                  onChange={(e) => setLabelFormData({ ...labelFormData, sendEmail: e.target.checked })}
                />
                <Mail size={18} />
                {t('admin.shipping.sendEmail')}
              </label>
              <small style={{ color: '#94a3b8', fontSize: '12px', display: 'block', marginTop: '0.5rem' }}>
                {t('admin.shipping.sendEmailHelp')}
              </small>
            </div>

            {labelFormData.sendEmail && (
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>{t('admin.shipping.emailLanguage')}</label>
                <select
                  value={labelFormData.language}
                  onChange={(e) => setLabelFormData({ ...labelFormData, language: e.target.value })}
                >
                  <option value="it">Italiano</option>
                  <option value="en">English</option>
                </select>
              </div>
            )}
          </div>

          {/* ── Modal footer actions ────────────────────────── */}
          <div className="modal-actions" style={{ marginTop: '2rem' }}>
            <button type="button" onClick={onClose} className="admin-btn">
              {t('admin.shipping.actions.cancel')}
            </button>

            {!labelFormData.sendEmail && (
              <button type="submit" className="admin-btn admin-btn-primary">
                <Download size={16} style={{ marginRight: '0.5rem' }} />
                {t('admin.shipping.actions.downloadLabel')}
              </button>
            )}

            {labelFormData.sendEmail && (
              <button type="submit" className="admin-btn admin-btn-primary">
                <Mail size={16} style={{ marginRight: '0.5rem' }} />
                {t('admin.shipping.actions.sendLabel')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShippingLabelModal;
